
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';

export default function AboutPage() {
  useEffect(() => {
    // SEO Meta tags for About page
    document.title = '会社概要 | 株式会社OnYourSide';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '株式会社OnYourSideの会社概要。2022年設立、資本金100万円。東京都品川区に本社を構え、人材育成・研修・人事コンサルティングサービスを提供しています。');
    }

    // Schema.org JSON-LD for About page
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "会社概要",
      "description": "株式会社OnYourSideの会社情報をご紹介します。",
      "url": `${import.meta.env.VITE_SITE_URL}/about`,
      "mainEntity": {
        "@type": "Organization",
        "name": "株式会社OnYourSide",
        "foundingDate": "2022",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "西五反田1-26-2 五反田サンハイツ911",
          "addressLocality": "品川区",
          "addressRegion": "東京都",
          "addressCountry": "JP"
        },
        "email": "info@oyside.com"
      }
    });
    document.head.appendChild(script);
    
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            会社概要
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            株式会社OnYourSideの会社情報をご紹介します
          </p>
        </div>
      </section>

      {/* 会社情報 */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">会社情報</h2>
            <div className="grid md:grid-cols-2 gap-12">
              <div className="bg-gray-50 p-8 rounded-xl">
                <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                  <i className="ri-building-line text-2xl text-blue-600 mr-3"></i>
                  基本情報
                </h3>
                <div className="space-y-4">
                  <div className="flex">
                    <span className="font-medium text-gray-700 w-20">会社名：</span>
                    <span className="text-gray-600">株式会社OnYourSide</span>
                  </div>
                  <div className="flex">
                    <span className="font-medium text-gray-700 w-20">設立：</span>
                    <span className="text-gray-600">2022年</span>
                  </div>
                  <div className="flex">
                    <span className="font-medium text-gray-700 w-20">資本金：</span>
                    <span className="text-gray-600">100万円</span>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 p-8 rounded-xl">
                <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                  <i className="ri-map-pin-line text-2xl text-blue-600 mr-3"></i>
                  所在地・連絡先
                </h3>
                <div className="space-y-4">
                  <div>
                    <span className="font-medium text-gray-700 block mb-1">所在地：</span>
                    <span className="text-gray-600">東京都品川区西五反田1-26-2<br />五反田サンハイツ911</span>
                  </div>
                  <div className="flex">
                    <span className="font-medium text-gray-700 w-20">Email：</span>
                    <a href="mailto:info@oyside.com" className="text-blue-600 hover:text-blue-800">
                      info@oyside.com
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 代表者プロフィール */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">代表者プロフィール</h2>
            <div className="grid md:grid-cols-2 gap-12 items-start">
              <div className="text-center">
                <img
                  src="https://static.readdy.ai/image/2db098dc71e4ecf917d1962c25013c7b/bbca2c8f0455c7e0e3dae8cadb819389.jpeg"
                  alt="代表者"
                  className="rounded-xl shadow-lg object-cover object-top w-full h-96 mb-6"
                />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">田中　麻衣子</h3>
                <p className="text-lg text-gray-600">代表コンサルタント</p>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">経歴・資格</h3>
                <div className="space-y-6">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">学位</h4>
                    <ul className="space-y-2 text-gray-600">
                      <li>• 修士（経営）　一橋大学</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">職歴</h4>
                    <ul className="space-y-2 text-gray-600">
                      <li>• 製造業、自治体、外資系金融機関、SIer、グローバルIT企業にてHR業務に従事</li>
                      <li>• 人事部長として人事業務全般を管掌</li>
                      <li>• 外資系金融機関ではセールス部門のトレーナーとして年間200日以上登壇</li>
                      <li>• 大学・団体等での登壇、外部研修講師として活動</li>
                      <li>• 2022年 株式会社OnYourSide設立</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">専門分野</h4>
                    <ul className="space-y-2 text-gray-600">
                      <li>• コミュニケーション研修</li>
                      <li>• リーダー・管理職研修</li>
                      <li>• 組織開発・人事制度設計</li>
                      <li>• セールストレーニング</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">強み・特徴</h4>
                    <ul className="space-y-2 text-gray-600">
                      <li>• 様々な業界での豊富な人事経験</li>
                      <li>• データと担当者インタビューによる課題分析</li>
                      <li>• 実践的で分かりやすい研修提供</li>
                      <li>• 組織の潜在的課題の発見と解決策提案</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-12 bg-blue-50 p-8 rounded-xl">
              <h3 className="text-xl font-bold text-gray-900 mb-4">メッセージ</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                製造業、自治体、外資系金融機関、SIer、グローバルIT企業と、多様な組織でのHR業務経験を積んでまいりました。
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                外資系金融機関では年間200日以上の登壇経験を通じて、実践的な研修スキルを磨きました。人事部長として人事業務全般を管掌した経験から、組織の課題を深く理解し、データと担当者インタビューを基にした的確な解決策をご提案いたします。
              </p>
              <p className="text-gray-700 leading-relaxed">
                様々な組織での実体験を踏まえた、分かりやすく実践的な研修で、企業様の成長をサポートしてまいります。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            お気軽にお問い合わせください
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            ご質問やご相談がございましたら、お気軽にお声かけください
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 whitespace-nowrap">
                お問い合わせ
              </Button>
            </Link>
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600 whitespace-nowrap">
                サービス一覧
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
